/**
 * PWA registration — service worker lifecycle + install prompt
 * plumbing. Exposes two callback hooks for the UI layer:
 *
 *   - `onNeedRefresh(reload)` — a fresh bundle is waiting in the
 *     wings. Calling `reload()` activates the new SW and reloads the
 *     page. Used by the PwaToast to show the "New version" prompt.
 *   - `onOfflineReady()` — first install completed, the app will
 *     work without a network connection from now on. Used to flash
 *     a one-shot "Ready offline" toast.
 *
 * Plus an install-prompt manager: browsers fire
 * `beforeinstallprompt` when the page is install-eligible. We
 * capture the event and stash it; the UI surfaces an "Install app"
 * button that calls `promptInstall()` to actually trigger the
 * native install dialog.
 */

import { registerSW } from 'virtual:pwa-register';

export interface PwaHooks {
  readonly onNeedRefresh?: (reload: () => Promise<void>) => void;
  readonly onOfflineReady?: () => void;
}

/**
 * Install-prompt state. Browsers gatekeep `prompt()` to a single
 * call per `beforeinstallprompt` event, so we keep the deferred
 * event around and only consume it on user click. After successful
 * install (`appinstalled`) we drop the reference and set
 * `isInstalled` so the UI hides the button.
 */
export interface InstallController {
  readonly canInstall: () => boolean;
  readonly isInstalled: () => boolean;
  readonly promptInstall: () => Promise<'accepted' | 'dismissed' | 'unavailable'>;
  readonly subscribe: (fn: (canInstall: boolean) => void) => () => void;
}

interface BeforeInstallPromptEvent extends Event {
  readonly platforms: readonly string[];
  readonly userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
  prompt(): Promise<void>;
}

/**
 * Wire up service worker registration + install-prompt capture.
 * Returns an `InstallController` the UI can subscribe to. The SW
 * skips registration in dev mode (vite-plugin-pwa convention).
 */
export function initPWA(hooks: PwaHooks = {}): InstallController {
  // ── Install-prompt capture ──────────────────────────────────────
  let deferred: BeforeInstallPromptEvent | null = null;
  let installed = isStandalone();
  const listeners = new Set<(canInstall: boolean) => void>();
  const emit = (): void => {
    const can = !installed && deferred !== null;
    for (const fn of listeners) fn(can);
  };

  window.addEventListener('beforeinstallprompt', (e: Event) => {
    // Suppress the default mini-infobar (Chrome desktop) — we surface
    // our own button instead.
    e.preventDefault();
    deferred = e as BeforeInstallPromptEvent;
    emit();
  });

  window.addEventListener('appinstalled', () => {
    installed = true;
    deferred = null;
    emit();
  });

  // ── Service worker registration ─────────────────────────────────
  if (!import.meta.env.DEV) {
    const update = registerSW({
      immediate: false,
      onNeedRefresh() {
        // The plugin doesn't pass us a reload function — we wrap
        // updateSW(true) which activates the waiting worker and
        // reloads the page in one step.
        hooks.onNeedRefresh?.(async () => {
          await update(true);
        });
      },
      onOfflineReady() {
        hooks.onOfflineReady?.();
      },
      onRegisterError(err) {
        console.warn('[pwa] registration failed', err);
      },
    });

    // Expose for a future "check for updates" UI button.
    (window as unknown as { __updatePWA: typeof update }).__updatePWA = update;
  } else {
    console.info('[pwa] dev mode — service worker disabled');
  }

  return {
    canInstall: () => !installed && deferred !== null,
    isInstalled: () => installed,
    async promptInstall(): Promise<'accepted' | 'dismissed' | 'unavailable'> {
      if (installed || !deferred) return 'unavailable';
      const event = deferred;
      // Browsers only allow one prompt per event — drop the reference
      // before awaiting so a double-click can't double-prompt.
      deferred = null;
      emit();
      try {
        await event.prompt();
        const choice = await event.userChoice;
        return choice.outcome;
      } catch (err) {
        console.warn('[pwa] install prompt failed', err);
        return 'dismissed';
      }
    },
    subscribe(fn) {
      listeners.add(fn);
      // Replay current state immediately.
      fn(!installed && deferred !== null);
      return () => {
        listeners.delete(fn);
      };
    },
  };
}

/**
 * True when the page is already running as an installed PWA. Saves
 * us prompting for install when the app is already standalone.
 */
function isStandalone(): boolean {
  if (typeof window === 'undefined') return false;
  if (window.matchMedia?.('(display-mode: standalone)').matches) return true;
  // iOS Safari uses a non-standard `navigator.standalone`.
  const nav = navigator as unknown as { standalone?: boolean };
  return nav.standalone === true;
}
